import React, { useEffect } from "react";
import jexcel, { CellValue, JExcelElement } from "jexcel";

import "../../../../node_modules/jexcel/dist/jexcel.css";
import {
  IClass,
  ISchoolManager,
  IStudent,
  IStudentForRegister,
  IStudentPlan,
} from "../../../state";
import { EStudentSortType, SortStudents } from "../../../libs/Util";
import { ModalContext } from "../../../libs/ModalProvider";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import useSchoolStatus from "../../../hooks/manager/useSchoolStatus";
import Confirm from "../../../molecules/Confirm";
import AlertModal from "../../../molecules/AlertModal";

import styles from "./StudentSpreadSheet.module.scss";

export interface StudentSpreadSheetProps {
  title: string;
  calling: boolean;
  error: string;
  students: IStudent[];
  classes: IClass[];
  next_classes?: IClass[];
  school: ISchoolManager;
  plans?: IStudentPlan[];
  isNextYear: boolean;
  save: (students: IStudentForRegister[]) => void;
  deleteStudents?: (student_ids: number[]) => void;
  failedStudentIds?: number[];
  isSelectMode?: boolean;
  onChangeSelectMode?: (value: boolean) => void;
  reset: () => void;
  isResetting?: boolean;
  onFilterByClass: (class_id: string) => void;
  onFilterByYear: (year: string) => void;
}

export const StudentSpreadSheet = (props: StudentSpreadSheetProps) => {
  const ctx = React.useContext(ModalContext);
  const { isCramSchool } = useSchoolStatus();
  const jexcelRef = React.useRef<HTMLDivElement>(null);
  const CSVInputRef = React.useRef<HTMLInputElement>(null);
  const [yearOptions, setYearOptions] = React.useState<number[]>([]);
  const [isDirty, setIsDirty] = React.useState(false);
  const [state, setState] = React.useState({
    sheet: null as null | JExcelElement,
    csvFile: null as null | File,
    csvRowsData: null as null | string[][],
  });
  const [sortBy, setSortBy] = React.useState<EStudentSortType>(
    EStudentSortType.id,
  );
  const { currentYear, nextYear, currentYearShortLabel, nextYearShortLabel } =
    useCurrentYear();

  const cols = React.useMemo(() => {
    const res = {
      identity: { index: 0, title: "ID" },
      last_name: { index: 1, title: "名字" },
      first_name: { index: 2, title: "名前" },
      last_name_kana: { index: 3, title: "名字(カナ)" },
      first_name_kana: { index: 4, title: "名前(カナ)" },
      student_no_in_school: { index: 5, title: "学籍番号" },
      email: { index: 6, title: "メールアドレス" },
      guardian_email: { index: 7, title: "保護者のメールアドレス" },
      entrance_year: { index: 8, title: "入学年" },
      current_grade: { index: 9, title: "現在の学年" },
      class: { index: 10, title: "クラス" },
      next_grade: { index: 11, title: "次の学年" },
    } as {
      [key: string]: { index: number; title: string; excelTitle?: string };
    };
    if (props.isNextYear) {
      res["current_grade"] = {
        index: 9,
        title: `${currentYearShortLabel}年度学年`,
        excelTitle: `${currentYearShortLabel}年度\n学年`,
      };
      res["class"] = {
        index: 10,
        title: `${currentYearShortLabel}年度クラス`,
        excelTitle: `${currentYearShortLabel}年度\nクラス`,
      };
      res["next_grade"] = {
        index: 11,
        title: `${nextYearShortLabel}年度学年`,
        excelTitle: `${nextYearShortLabel}年度\n学年`,
      };
      res["next_class"] = {
        index: 12,
        title: `${nextYearShortLabel}年度クラス`,
        excelTitle: `${nextYearShortLabel}年度\nクラス`,
      };
      res["dlp_enabled"] = { index: 13, title: "教材対象" };
      res["g4s_enabled"] = { index: 14, title: "GEAR拡張" };
      if (isCramSchool) {
        res["plan"] = { index: 15, title: "プラン" };
        res["check"] = { index: 16, title: "チェック結果" };
      } else {
        res["check"] = { index: 15, title: "チェック結果" };
      }
    } else {
      res["current_grade"] = {
        index: 9,
        title: "現在の学年",
        excelTitle: "現在の学年",
      };
      res["class"] = { index: 10, title: "クラス", excelTitle: "クラス" };
      res["next_grade"] = {
        index: 11,
        title: "次の学年",
        excelTitle: "次の学年",
      };
      res["dlp_enabled"] = { index: 12, title: "教材対象" };
      res["g4s_enabled"] = { index: 13, title: "GEAR拡張" };
      if (isCramSchool) {
        res["plan"] = { index: 14, title: "プラン" };
        res["check"] = { index: 15, title: "チェック結果" };
      } else {
        res["check"] = { index: 14, title: "チェック結果" };
      }
    }
    return res;
  }, [
    props.isNextYear,
    currentYearShortLabel,
    nextYearShortLabel,
    isCramSchool,
  ]);

  const requiredFields = React.useMemo(() => {
    const listFieldsRequired: string[] = [
      "last_name",
      "first_name",
      "last_name_kana",
      "first_name_kana",
      "entrance_year",
      "next_grade",
    ];
    if (!props.isNextYear) listFieldsRequired.push("current_grade");
    return listFieldsRequired;
  }, [props.isNextYear]);

  const sortedStudents = React.useMemo(() => {
    const students = props.students;
    SortStudents(students, sortBy);

    return [...students];
  }, [props.students, sortBy]);

  const studentsByIdentity = React.useMemo(
    () => new Map(sortedStudents.map(s => [s.identity, s])),
    [sortedStudents],
  );

  React.useEffect(() => {
    if (!props.failedStudentIds?.length) return;
    const names = props.failedStudentIds
      .map(id => {
        const student = sortedStudents.find(student => student.id === id);
        return student ? `${student.last_name} ${student.first_name}` : "";
      })
      .filter(Boolean)
      .join("、");
    ctx.actions.showModal(
      <AlertModal
        content={
          <>
            以下の生徒は利用データがあるため削除できません。
            <br />
            {names}
          </>
        }
        closeModal={() => ctx.actions.closeModal()}
        callback={() => ctx.actions.closeModal()}
      />,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.failedStudentIds]);

  const save = props.save;
  const colWidths = React.useMemo(() => {
    // 年度更新（塾）
    if (props.isNextYear && isCramSchool) {
      return [
        100, 100, 100, 100, 100, 140, 140, 170, 80, 80, 80, 80, 80, 80, 80, 250,
        300,
      ];
    }
    // 年度更新（学校）
    else if (props.isNextYear) {
      return [
        100, 100, 100, 100, 100, 140, 140, 170, 80, 80, 80, 80, 80, 80, 80, 300,
      ];
    }
    // 塾
    else if (isCramSchool) {
      return [
        120, 100, 100, 100, 100, 140, 140, 170, 80, 80, 80, 80, 80, 80, 250,
        300,
      ];
    }
    // 学校
    else {
      return [
        120, 100, 100, 100, 100, 140, 140, 170, 80, 80, 80, 80, 80, 80, 300,
      ];
    }
  }, [props.isNextYear, isCramSchool]);
  const regexCheckEmail = /\S+@\S+\.\S+/;

  const validates: ((row: string[]) => string | null)[] = React.useMemo(() => {
    // validate for entrance_year
    const v1 = (row: string[]) => {
      const entranceYear = Number(row[cols["entrance_year"].index]);
      if (
        !entranceYear ||
        (!props.isNextYear && entranceYear > currentYear) ||
        (props.isNextYear &&
          (row[cols["current_grade"].index]
            ? entranceYear > currentYear
            : entranceYear !== nextYear))
      ) {
        return "entrance_year";
      }
      return null;
    };

    // validate for current_grade
    const v2 = (row: string[]) => {
      const currentGrade = Number(row[cols["current_grade"].index]);
      const nextGrade = Number(row[cols["next_grade"].index]);
      const entranceYear = Number(row[cols["entrance_year"].index]);
      if (
        !!currentGrade
          ? (!!nextGrade && nextGrade < currentGrade) ||
            entranceYear === nextYear
          : entranceYear <= currentYear
      ) {
        return "current_grade";
      }
      return null;
    };

    // validate for next_grade
    const v3 = (row: string[]) => {
      const currentGrade = Number(row[cols["current_grade"].index]);
      const nextGrade = Number(row[cols["next_grade"].index]);
      if (!nextGrade || (!!currentGrade && nextGrade < currentGrade)) {
        return "next_grade";
      }
      return null;
    };

    // validate for next_class
    const v4 = (row: string[]) => {
      if (
        !row[cols["current_grade"].index] &&
        !row[cols["next_class"]?.index]
      ) {
        return "next_class";
      }
      return null;
    };

    // validate for dlp_enabled
    const v5 = (row: string[]) => {
      const materialCheckbox = row[cols["dlp_enabled"].index];
      if (materialCheckbox !== "true" && materialCheckbox !== "false") {
        return "dlp_enabled";
      }
      return null;
    };

    // validate for g4s_enabled
    const v6 = (row: string[]) => {
      const gearCheckbox = row[cols["g4s_enabled"].index];
      if (gearCheckbox !== "true" && gearCheckbox !== "false") {
        return "g4s_enabled";
      }
      return null;
    };

    return [v1, v2, v3, v4, v5, v6];
  }, [props.isNextYear, currentYear, nextYear, cols]);

  useEffect(() => {
    const options: number[] = [];
    sortedStudents.forEach(st => {
      if (!options.includes(st.entrance_year)) {
        options.push(st.entrance_year);
      }
    });
    options.sort();
    setYearOptions(options);
  }, [sortedStudents]);

  useEffect(() => {
    const jexcelRefCurrent = jexcelRef.current;
    if (jexcelRefCurrent) {
      jexcelRefCurrent.innerHTML = "";
    }
    if (!state.csvRowsData) setIsDirty(false);
    let sheet = null as null | JExcelElement;

    const buildRowData = (s: IStudent) => {
      const cls = props.classes.find(c => c.id === s.class_id);
      const nextCls = props.next_classes?.find(nC => nC.id === s.next_klass_id);
      const activePlanNames =
        s.active_plans
          ?.map(
            plan => props.plans?.find(p => p.id === plan.plan_id)?.name || "",
          )
          .join(";") || "";
      return [
        s.identity,
        s.last_name,
        s.first_name,
        s.last_name_kana,
        s.first_name_kana,
        s.student_no_in_school,
        s.email,
        s.guardian_email,
        s.entrance_year,
        s.current_grade,
        cls?.id || "",
        s.next_grade,
        ...(props.isNextYear ? [nextCls?.id || ""] : []),
        s.dlp_enabled,
        s.g4s_enabled,
        ...(isCramSchool ? [activePlanNames] : []),
        "",
      ];
    };

    const buildSelectModeData = (s: IStudent) => {
      const activePlanNames =
        s.active_plans
          ?.map(
            plan => props.plans?.find(p => p.id === plan.plan_id)?.name || "",
          )
          .join(";") || "";
      return [
        false,
        s.identity,
        s.last_name,
        s.first_name,
        s.last_name_kana,
        s.first_name_kana,
        s.student_no_in_school || "",
        s.email || "",
        s.guardian_email || "",
        s.entrance_year,
        s.current_grade,
        props.classes.find(c => c.id === s.class_id)?.name || "",
        s.next_grade,
        ...(props.isNextYear
          ? [
              props.next_classes?.find(nC => nC.id === s.next_klass_id)?.name ||
                "",
            ]
          : []),
        s.dlp_enabled ? "○" : "×",
        s.g4s_enabled ? "○" : "×",
        ...(isCramSchool ? [activePlanNames] : []),
        s.can_destroy !== true ? "利用データがあるため削除できません" : "",
      ];
    };

    const selectModeColumns = [
      { type: "checkbox" as const, title: "選択" },
      { type: "text" as const, title: cols["identity"].title, readOnly: true },
      { type: "text" as const, title: cols["last_name"].title, readOnly: true },
      {
        type: "text" as const,
        title: cols["first_name"].title,
        readOnly: true,
      },
      {
        type: "text" as const,
        title: cols["last_name_kana"].title,
        readOnly: true,
      },
      {
        type: "text" as const,
        title: cols["first_name_kana"].title,
        readOnly: true,
      },
      {
        type: "text" as const,
        title: cols["student_no_in_school"].title,
        readOnly: true,
      },
      { type: "text" as const, title: cols["email"].title, readOnly: true },
      {
        type: "text" as const,
        title: cols["guardian_email"].title,
        readOnly: true,
      },
      {
        type: "text" as const,
        title: cols["entrance_year"].title,
        readOnly: true,
      },
      {
        type: "text" as const,
        title: cols["current_grade"].title,
        readOnly: true,
      },
      { type: "text" as const, title: cols["class"].title, readOnly: true },
      {
        type: "text" as const,
        title: cols["next_grade"].title,
        readOnly: true,
      },
      ...(props.isNextYear
        ? [
            {
              type: "text" as const,
              title: cols["next_class"].title,
              readOnly: true,
            },
          ]
        : []),
      {
        type: "text" as const,
        title: cols["dlp_enabled"].title,
        readOnly: true,
      },
      {
        type: "text" as const,
        title: cols["g4s_enabled"].title,
        readOnly: true,
      },
      ...(isCramSchool
        ? [{ type: "text" as const, title: cols["plan"].title, readOnly: true }]
        : []),
      { type: "text" as const, title: "チェック結果", readOnly: true },
    ];

    const editModeColumns = [
      { type: "text" as const, title: cols["identity"].title, readOnly: true },
      { type: "text" as const, title: `${cols["last_name"].title}*` },
      { type: "text" as const, title: `${cols["first_name"].title}*` },
      { type: "text" as const, title: `${cols["last_name_kana"].title}*` },
      { type: "text" as const, title: `${cols["first_name_kana"].title}*` },
      { type: "text" as const, title: cols["student_no_in_school"].title },
      { type: "text" as const, title: cols["email"].title },
      { type: "text" as const, title: cols["guardian_email"].title },
      { type: "numeric" as const, title: `${cols["entrance_year"].title}*` },
      {
        type: "numeric" as const,
        title: `${cols["current_grade"].excelTitle}*`,
        readOnly: props.isNextYear,
      },
      {
        type: "dropdown" as const,
        title: cols["class"].excelTitle,
        source: props.classes.map(c => ({
          id: c.id,
          name: c.name,
          grade: c.grade,
        })),
        filter: (
          instance: any,
          cell: HTMLTableCellElement,
          c: number,
          r: any,
          source: any,
        ) => {
          const currentGrade = instance.jexcel.getValueFromCoords(
            cols["current_grade"].index,
            r,
          );
          if (currentGrade !== "") {
            return source.filter(
              (item: { grade: number }) => item.grade === Number(currentGrade),
            );
          }
          return [];
        },
        readOnly: props.isNextYear,
      },
      { type: "numeric" as const, title: `${cols["next_grade"].excelTitle}*` },
      ...(props.isNextYear
        ? [
            {
              type: "dropdown" as const,
              title: cols["next_class"].excelTitle,
              source: (props.next_classes || []).map(nC => ({
                id: nC.id,
                name: nC.name,
                grade: nC.grade,
              })),
              filter: (
                instance: any,
                cell: HTMLTableCellElement,
                c: number,
                r: any,
                source: any,
              ) => {
                const nextGrade = instance.jexcel.getValueFromCoords(
                  cols["next_grade"].index,
                  r,
                );
                if (nextGrade !== "") {
                  return source.filter(
                    (item: { grade: number }) =>
                      item.grade === Number(nextGrade),
                  );
                }
                return [];
              },
            },
          ]
        : []),
      {
        type: "checkbox" as const,
        title: cols["dlp_enabled"].title,
        readOnly: !(props.school.total_dlp_registrable_num! > 0),
      },
      {
        type: "checkbox" as const,
        title: cols["g4s_enabled"].title,
        readOnly: !(props.school.total_g4s_registrable_num! > 0),
      },
      ...(isCramSchool
        ? [
            {
              type: "dropdown" as const,
              title: cols["plan"].title,
              source: props.plans?.map(plan => plan.name) ?? [],
              multiple: true,
            },
          ]
        : []),
      { type: "text" as const, title: cols["check"].title },
    ];

    const options = {
      data: (() => {
        if (props.isSelectMode) {
          return sortedStudents.length
            ? sortedStudents.map(buildSelectModeData)
            : [[]];
        }
        if (state.csvRowsData) return state.csvRowsData;
        return sortedStudents.length ? sortedStudents.map(buildRowData) : [[]];
      })(),
      minSpareRows: props.isSelectMode ? 0 : 2,
      colWidths: props.isSelectMode ? [50, ...colWidths] : colWidths,
      columns: props.isSelectMode ? selectModeColumns : editModeColumns,
      updateTable: (
        el: any,
        cell: HTMLTableCellElement,
        x: number,
        y: number,
        _source: any,
        _value: any,
        _id: any,
      ) => {
        if (props.isSelectMode) {
          const identity = String(
            el.jexcel?.getValueFromCoords(1, y, false) || "",
          );
          const student = studentsByIdentity.get(identity);
          if (x === 0 && student?.can_destroy !== true) {
            cell.classList.add("readonly");
            const checkbox = cell.querySelector('input[type="checkbox"]');
            if (checkbox) (checkbox as HTMLInputElement).disabled = true;
          }
          if (student?.can_destroy === true && x > 1) {
            cell.style.color = "#000";
          }
          return;
        }
        sortedStudents.forEach((s, i) => {
          if (
            (props.isNextYear &&
              s.entrance_year <= currentYear &&
              y === i &&
              [
                cols["last_name"].index,
                cols["first_name"].index,
                cols["last_name_kana"].index,
                cols["first_name_kana"].index,
                cols["entrance_year"].index,
              ].includes(x)) ||
            (isCramSchool &&
              s.plan_disabled &&
              y === i &&
              x === cols["plan"].index)
          ) {
            cell.classList.add("readonly");

            // plan_disabledがtrueの場合は「終了」と表示
            if (isCramSchool && s.plan_disabled && x === cols["plan"].index) {
              cell.innerHTML = "終了";
            }
          }
        });
      },
      onbeforechange: (
        instance: HTMLElement,
        cell: HTMLTableCellElement,
        x: number,
        y: number,
        value: any,
      ) => {
        if (props.isSelectMode && x === 0) {
          const identity = String(sheet?.getValueFromCoords(1, y, false) || "");
          const student = studentsByIdentity.get(identity);
          if (student?.can_destroy !== true) {
            return sheet?.getValueFromCoords(0, y, false);
          }
        }

        const rec = sheet?.getRowData(Number(y));

        if (rec && `${x}` === `${cols["class"].index}`) {
          const grade = Number(rec[cols["current_grade"].index]);
          const classesByGrade = props.classes.filter(cl => cl.grade === grade);
          const currentClassID = rec[cols["class"].index];
          return (
            classesByGrade.find(cl => cl.name === value)?.id ||
            classesByGrade.find(cl => cl.id === Number(value))?.id ||
            currentClassID
          );
        }

        if (
          props.next_classes &&
          rec &&
          `${x}` === `${cols["next_class"].index}`
        ) {
          const nextGrade = Number(rec[cols["next_grade"].index]);
          const nextClassesByGrade = props.next_classes.filter(
            cl => cl.grade === nextGrade,
          );
          const currentNextClassID = rec[cols["next_class"].index];
          return (
            nextClassesByGrade.find(cl => cl.name === value)?.id ||
            nextClassesByGrade.find(cl => cl.id === Number(value))?.id ||
            currentNextClassID
          );
        }
      },
      onchange: (
        _instance: HTMLElement,
        _cell: HTMLTableCellElement,
        x: number,
        y: number,
        _value: any,
      ) => {
        if (props.isSelectMode && x === 0) {
          return;
        }
        setIsDirty(true);
        const rec = sheet?.getRowData(Number(y));
        let result = "";
        if (
          rec &&
          (`${x}` === `${cols["email"].index}` ||
            `${x}` === `${cols["guardian_email"].index}` ||
            `${x}` === `${cols["last_name"].index}` ||
            `${x}` === `${cols["first_name"].index}` ||
            `${x}` === `${cols["last_name_kana"].index}` ||
            `${x}` === `${cols["first_name_kana"].index}` ||
            `${x}` === `${cols["current_grade"].index}` ||
            `${x}` === `${cols["next_grade"].index}` ||
            `${x}` === `${cols["next_class"]?.index}` ||
            `${x}` === `${cols["entrance_year"].index}` ||
            (isCramSchool && `${x}` === `${cols["plan"].index}`))
        ) {
          const grade = Number(rec[cols["current_grade"].index]);
          const nextGrade = rec[cols["next_grade"].index]
            ? Number(rec[cols["next_grade"].index])
            : 0;
          const entranceYear = Number(rec[cols["entrance_year"].index]);
          if (rec[cols["email"].index]) {
            if (!regexCheckEmail.test(rec[cols["email"].index])) {
              result = "不正なメールアドレスが入力されています";
            }
          }
          if (rec[cols["guardian_email"].index]) {
            if (!regexCheckEmail.test(rec[cols["guardian_email"].index])) {
              result = "不正なメールアドレスが入力されています";
            }
          }
          requiredFields.forEach(key => {
            if (!rec[cols[key].index]) {
              result = `${cols[key].title}が必要です`;
            }
            if (isCramSchool && !rec[cols["plan"].index]) {
              const studentId = rec[cols["identity"].index];
              const student = sortedStudents.find(
                s => s.identity === studentId,
              );

              if (!student?.plan_disabled) {
                result = "プランが選択されていません";
              }
            }
          });
          if (!!grade && !!nextGrade && grade > nextGrade) {
            result = "現在の学年と次の学年に不整合があります";
          }
          if (props.isNextYear) {
            if (!grade && entranceYear && entranceYear !== nextYear) {
              result = `入学年は${nextYear}年でなければなりません`;
            }
          } else {
            if (entranceYear && entranceYear > currentYear) {
              result = `入学年は${currentYear}年以下でなければなりません`;
            }
          }
          sheet?.setValueFromCoords(
            cols["check"].index,
            Number(y),
            result,
            false,
          );
        }
      },
    };
    sheet = jexcel(jexcelRefCurrent as any, options as any);
    setState({ ...state, sheet });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.classes, sortedStudents, state.csvRowsData, props.isSelectMode]);

  const addRow = React.useCallback(() => {
    state.sheet?.insertRow();
  }, [state.sheet]);

  const discardEdits = React.useCallback(() => {
    CSVInputRef.current && (CSVInputRef.current.value = "");
    setIsDirty(false);
    setState(prevState => ({
      ...prevState,
      csvRowsData: null,
      csvFile: null,
    }));
    props.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveFunc = React.useCallback(() => {
    const data = [] as IStudentForRegister[];
    let isInvalidEmail = false;
    if (state.sheet) {
      const dataSet = state.sheet?.getData();
      dataSet.forEach((d, i) => {
        const isAllRequiredFieldsValid = requiredFields.every(
          key => !!d[cols[key].index],
        );
        const emailValue = d[cols["email"].index] as string;
        const guardianEmailValue = d[cols["guardian_email"].index] as string;
        if (
          (emailValue.length && !regexCheckEmail.test(emailValue)) ||
          (guardianEmailValue.length &&
            !regexCheckEmail.test(guardianEmailValue))
        ) {
          isInvalidEmail = true;
        }
        const planData =
          isCramSchool &&
          d[cols["plan"].index] &&
          d[cols["plan"].index] !== "終了"
            ? d[cols["plan"].index]
                .toString()
                .split(";")
                .reduce((acc, planName) => {
                  const planId = props.plans?.find(
                    p => p.name.trim() === planName.trim(),
                  )?.id;
                  if (planId) acc.push(planId);
                  return acc;
                }, [] as number[])
            : null;
        if (isAllRequiredFieldsValid && !d[cols["check"].index]) {
          data.push({
            identity: d[cols["identity"].index] as string,
            last_name: d[cols["last_name"].index] as string,
            first_name: d[cols["first_name"].index] as string,
            last_name_kana: d[cols["last_name_kana"].index] as string,
            first_name_kana: d[cols["first_name_kana"].index] as string,
            student_no_in_school: d[
              cols["student_no_in_school"].index
            ] as string,
            email: (d[cols["email"].index] as string) || null,
            guardian_email: (d[cols["guardian_email"].index] as string) || null,
            entrance_year: d[cols["entrance_year"].index] as number,
            current_grade: (d[cols["current_grade"].index] as number) || null,
            next_grade: d[cols["next_grade"].index] as number,
            class_id: (d[cols["class"].index] as number) || null,
            next_class_id:
              (!props.isNextYear
                ? sortedStudents[i]?.next_klass_id
                : (d[cols["next_class"].index] as number)) || null,
            g4s_enabled: d[cols["g4s_enabled"].index] as boolean,
            dlp_enabled: d[cols["dlp_enabled"].index] as boolean,
            plan_ids: isCramSchool && planData?.length ? planData : null,
          });
        }
      });
    }
    if (isInvalidEmail) {
      ctx.actions.showModal(
        <AlertModal
          closeModal={() => {
            ctx.actions.closeModal();
          }}
          content={
            <>
              <div>更新できませんでした。</div>
              <div>正しいメールアドレスを入力してください。</div>
            </>
          }
          callback={() => {
            ctx.actions.closeModal();
          }}
        />,
      );
    } else save(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    save,
    currentYear,
    state.sheet,
    sortedStudents,
    props.isNextYear,
    props.plans,
    isCramSchool,
  ]);

  const handleBulkDelete = React.useCallback(() => {
    if (!props.deleteStudents) return;
    const data = state.sheet?.getData() || [];
    const selectedIds = data
      .filter(row => row[0] === true || row[0] === "true")
      .map(row => {
        const identity = String(row[1]);
        return studentsByIdentity.get(identity)?.id ?? null;
      })
      .filter((id): id is number => id !== null);

    if (selectedIds.length === 0) {
      ctx.actions.showModal(
        <AlertModal
          content="削除する生徒を選択してください。"
          closeModal={() => ctx.actions.closeModal()}
          callback={() => ctx.actions.closeModal()}
        />,
      );
      return;
    }

    ctx.actions.showModal(
      <Confirm
        content={`選択した${selectedIds.length}名の生徒を削除しますが、よろしいですか？`}
        closeModal={() => ctx.actions.closeModal()}
        callback={() => {
          ctx.actions.closeModal();
          props.deleteStudents!(selectedIds);
        }}
      />,
    );
  }, [state.sheet, studentsByIdentity, props, ctx.actions]);

  const findClassIdByGradeAndName = React.useCallback(
    (classes: IClass[], className: string, grade: number) => {
      return (
        classes.filter(c => c.grade === grade).find(c => c.name === className)
          ?.id || ""
      );
    },
    [],
  );

  const showErrorMessage = React.useCallback(
    (message: string) => {
      discardEdits();
      ctx.actions.showModal(
        <Confirm
          content={message}
          closeModal={ctx.actions.closeModal}
          callback={ctx.actions.closeModal}
        />,
      );
    },
    [ctx.actions, discardEdits],
  );

  const handleImportCSV = React.useCallback(
    (event: any) => {
      event.persist();
      if (event.target.files[0]) {
        const reader = new FileReader();
        reader.readAsText(event.target.files[0]);
        reader.onload = e => {
          const CSVString = e.target?.result as string;
          const handledDelimData = CSVString.replace(/;/g, ",");
          const headers = [
            ...handledDelimData
              .slice(0, handledDelimData.indexOf("\n"))
              .replace("\r", "")
              .split(","),
            "チェック結果",
          ];
          const dataRows = handledDelimData
            .slice(handledDelimData.indexOf("\n") + 1)
            .replace(/\r/g, "")
            .split("\n")
            .slice(1)
            .map((rowText: string) => {
              const rowArray = rowText.split(",");
              const handledRow = [
                ...rowArray.map((value: any, index) => {
                  if (index === cols["identity"].index) {
                    return value.replace("identity", "").trim();
                  }
                  if (index === cols["class"].index) {
                    return findClassIdByGradeAndName(
                      props.classes,
                      value,
                      Number(rowArray[cols["current_grade"].index]),
                    );
                  }
                  if (index === cols["next_class"]?.index) {
                    return value
                      ? findClassIdByGradeAndName(
                          props.next_classes || [],
                          value,
                          Number(rowArray[cols["next_grade"].index]),
                        )
                      : "";
                  }
                  if (isCramSchool && index === cols["plan"].index) {
                    return value.trim();
                  }
                  const toLowerCaseValue = value.trim().toLowerCase();
                  return toLowerCaseValue === "true" ||
                    toLowerCaseValue === "false"
                    ? toLowerCaseValue
                    : value.trim();
                }),
                "",
              ];
              return handledRow;
            })
            .filter((row: string[]) => row[1] || row[2] || row[3] || row[4]);
          const importedStudentIdList = dataRows
            ? JSON.parse(JSON.stringify(dataRows))
                .filter((row: string[]) => row[0])
                .map((row: string) => row[0])
                .sort()
            : null;
          const studentIdList = JSON.parse(JSON.stringify(sortedStudents))
            .map((student: IStudent) => student.identity)
            .sort();
          const expectedHeadersRaw = state.sheet?.getHeaders(true) || "";
          const expectedRawStr = expectedHeadersRaw
            .toString()
            .replace(/\n/g, "");
          const expectedHeadersArray: string[] = expectedRawStr.split(",");
          const missingColumns = expectedHeadersArray.filter(
            header => !headers.map(h => h.replace(/\n/g, "")).includes(header),
          );

          if (missingColumns.length > 0) {
            showErrorMessage(
              `「${missingColumns.join(
                ", ",
              )}」の項目が不足しています。再度CSVをダウンロードし、インポートし直してください。`,
            );
          } else if (headers.toString() !== expectedRawStr) {
            showErrorMessage(
              "テーブルの形式が間違っています。ファイルをもう一度確認してください。",
            );
          } else if (!importedStudentIdList || !dataRows) {
            showErrorMessage(
              "データがありません。ファイルをもう一度確認してください。",
            );
          } else if (
            studentIdList.toString() !== importedStudentIdList.toString()
          ) {
            showErrorMessage(
              "IDが一致しませんでした。既存行のIDは変更せず、新規追加する行はID列を空欄にしてください。",
            );
          } else {
            const errorPosition = [] as { header: string; rowNum: number }[];
            dataRows.forEach((row, rowIndex) => {
              const rowNum = rowIndex + 2;
              // Check required fields
              requiredFields.forEach(key => {
                if (!row[cols[key].index]) {
                  errorPosition.push({
                    header: cols[key].title,
                    rowNum,
                  });
                }
              });
              // Validate by functions
              validates.forEach(v => {
                const key = v(row);
                if (key) {
                  const hasPosition = errorPosition.some(
                    p => p.header === cols[key].title && p.rowNum === rowNum,
                  );
                  !hasPosition &&
                    errorPosition.push({
                      header: cols[key].title,
                      rowNum,
                    });
                }
              });
            });
            if (!errorPosition.length) {
              setIsDirty(true);
              setState(prevState => ({
                ...prevState,
                csvRowsData: dataRows,
                csvFile: event.target.files[0],
              }));
            } else {
              const message = `無効なデータがあります。以下の行を再度確認してください。${errorPosition
                .map(position => `[${position.rowNum}行目：${position.header}]`)
                .join("、")}`;
              showErrorMessage(message);
            }
          }
        };
      }
    },
    [
      sortedStudents,
      state.sheet,
      findClassIdByGradeAndName,
      showErrorMessage,
      props.classes,
      props.next_classes,
      isCramSchool,
      requiredFields,
      validates,
      cols,
    ],
  );

  const findClassNameById = React.useCallback(
    (classes: IClass[], classId: number) => {
      return classes.find(c => c.id === classId)?.name || "";
    },
    [],
  );

  const exportCSV = React.useCallback(
    (rowsData: CellValue[][], headers: string[], fileName?: string) => {
      if (!rowsData?.length || !headers?.length) {
        return;
      }
      const handledHeaders =
        headers
          .filter((_, index: number) => index !== cols["check"].index)
          .join(",")
          .replace(/\n/g, "") + "\n";

      const examples = [
        "新しく生徒を登録する場合は何も記入しないでください。", // identity
        "例：山田", // last_name
        "例：太郎", // first_name
        "例：ヤマダ", // last_name_kana
        "例：タロウ", // first_name_kana
        "", // student_no_in_school
        "生徒のGmailアドレス、またはMicrosoftのアドレスを登録した場合、ログインID/パスワードを入力しなくてもログインが可能です。", // email
        "yamada_hogosya@example.com", // guardian_email
        "例：2023", // entrance_year
        "例：1", // current_grade
        "例：3", // class
        "例：2", // next_grade: { index: 10, title: "次の学年" },
      ];
      if (props.isNextYear) {
        examples.push("例：1"); // next_class
      }
      examples.push(
        "デジタル教材を利用する場合は「TRUE」、不要な場合は「FALSE」と入力してください。",
      ); // dlp_enabled
      examples.push(
        "GEAR拡張を有効にする場合は「TRUE」、不要な場合は「FALSE」と入力してください。",
      ); // g4s_enabled
      if (isCramSchool) {
        examples.push("例：プレミアム"); // plan
      }
      const exampleRow = examples.join(",") + "\n";

      const handledRows = rowsData
        .map((row: CellValue[]) =>
          row
            .filter((_, index: number) => index !== cols["check"].index)
            .map((cell: CellValue, index: number) => {
              if (index === cols["identity"].index && cell)
                return "identity" + cell;
              if (index === cols["class"].index)
                return findClassNameById(props.classes, Number(cell));
              if (props.next_classes && index === cols["next_class"].index)
                return findClassNameById(props.next_classes, Number(cell));
              return cell;
            })
            .join(","),
        )
        .join("\n");

      const BOM = "\uFEFF";
      const csvContent = BOM + handledHeaders + exampleRow + handledRows;
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", fileName || "CSV_sheet.csv");
      document.body.appendChild(link);
      link.click();
    },
    [
      findClassNameById,
      props.classes,
      props.next_classes,
      props.isNextYear,
      isCramSchool,
      cols,
    ],
  );

  return (
    <div className={styles.wrapper}>
      {props.calling && (props.isSelectMode || !!props.isResetting) && (
        <div className={styles.overlay} />
      )}
      {props.error && (
        <div
          style={{
            padding: "20px",
            border: "2px solid #d84429",
            lineHeight: "1rem",
            marginBottom: "20px",
          }}
        >
          {props.error}
        </div>
      )}
      <div className={styles.header}>
        <h4>{props.title}</h4>
        <p>
          生徒のご登録、編集が可能です。 利用方法については、
          <a
            href="https://www.enageed.jp/hubfs/servicemanual/manager.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            学校管理機能マニュアル
          </a>
          もご覧ください。
          <br />
        </p>
        <p>
          ※納品指定日より前に生徒登録を行う場合、「教材対象」のチェックマークを外して登録ください。
          <br />
          納品後に改めて一括編集機能により「教材対象」の更新が可能です。
        </p>
        {isCramSchool && (
          <p>
            ※生徒情報をExcel（エクセル）などで管理されている場合は、下の表に「コピー&ペースト」で貼り付けて登録することができます。
          </p>
        )}
        {!isCramSchool && sortedStudents.length === 0 ? (
          <p className={styles.danger}>
            ※最初にインポート用CSVファイルをダウンロードしてください。
          </p>
        ) : null}
      </div>
      <div
        className={`${styles.actionButtons}${
          props.isSelectMode ? ` ${styles.selectMode}` : ""
        }`}
      >
        <button
          className={`btn btn-outline-danger me-3`}
          disabled={props.calling || props.isSelectMode}
          onClick={() => {
            ctx.actions.showModal(
              <Confirm
                calling={props.calling}
                content={"データを元の状態にリセットしますか？"}
                closeModal={() => {
                  ctx.actions.closeModal();
                }}
                callback={discardEdits}
              />,
            );
          }}
        >
          リセット
        </button>
        {!isCramSchool && (
          <>
            <button
              className={`btn btn-outline ${styles.utilBtn} me-3`}
              disabled={props.isSelectMode}
              onClick={() => {
                ctx.actions.showModal(
                  <Confirm
                    content={
                      <span>
                        CSVファイルをエクスポートします。
                        <br />
                        <br />
                        生徒データを新規追加する際は、エクスポートしたCSVファイル内の生徒データを残したまま、最下行からデータを追加し「ID」は空欄としてください。2行目は記入例となりますので消さないでください。
                      </span>
                    }
                    closeModal={() => {
                      ctx.actions.closeModal();
                    }}
                    callback={() => {
                      exportCSV(
                        state.sheet?.getData() || [],
                        state.sheet?.getHeaders(true) || [],
                        props.isNextYear
                          ? "next_student_list.csv"
                          : "student_list.csv",
                      );
                    }}
                  />,
                );
              }}
            >
              {sortedStudents.length === 0
                ? "インポート用CSVをダウンロード"
                : "CSVエクスポート"}
            </button>
            <button
              className={`btn btn-outline-primary me-3 ${styles.importCsv}`}
              onClick={() => CSVInputRef.current?.click()}
              disabled={props.isSelectMode}
            >
              CSVインポート
              <input
                ref={CSVInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
              />
            </button>
            {props.deleteStudents && (
              <button
                className="btn btn-outline-danger me-3"
                onClick={() => {
                  if (props.isSelectMode) {
                    props.onChangeSelectMode?.(false);
                  } else if (isDirty) {
                    ctx.actions.showModal(
                      <Confirm
                        calling={props.calling}
                        content={
                          "編集中の内容は破棄されますが、よろしいですか？"
                        }
                        closeModal={() => ctx.actions.closeModal()}
                        callback={() => {
                          ctx.actions.closeModal();
                          discardEdits();
                          props.onChangeSelectMode?.(true);
                        }}
                      />,
                    );
                  } else {
                    props.onChangeSelectMode?.(true);
                  }
                }}
              >
                {props.isSelectMode ? "キャンセル" : "登録を削除"}
              </button>
            )}
            {state.csvFile && (
              <p className={styles.fileName}>{state.csvFile.name}</p>
            )}
          </>
        )}
      </div>
      <div className={styles.control}>
        <div className={styles.search}>
          <div className="d-flex flex-column align-items-center me-3">
            <select
              name="next_grade"
              className="form-select"
              aria-label="select"
              onChange={e => props.onFilterByClass(e.target.value)}
            >
              <option value="">クラス</option>
              {[...props.classes, ...(props.next_classes || [])].map(m => {
                return (
                  <option key={m.id} value={m.id}>
                    {m.full_name} ({m.year})
                  </option>
                );
              })}
            </select>
          </div>
          <div className="d-flex flex-column align-items-center me-3">
            <select
              name="next_grade"
              className="form-select"
              aria-label="select"
              onChange={e => props.onFilterByYear(e.target.value)}
            >
              <option value="">入学年</option>
              {yearOptions.map(y => {
                return (
                  <option key={y} value={y}>
                    {y}年
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className={styles.filter}>
          <label className="ms-2 me-2">並び順</label>
          <div className="d-flex flex-column align-items-center">
            <select
              name="sortBy"
              className="form-select"
              aria-label="select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value as EStudentSortType)}
            >
              {Object.values(EStudentSortType).map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className={styles.excel} ref={jexcelRef} />
      <p className={styles.note}>
        {props.isSelectMode
          ? "※「チェック結果」にエラーが表示されている行は削除できません。"
          : "※「チェック結果」にエラーが表示されている行は登録されませんので、エラーを修正してから登録してください。"}
      </p>
      <div className={styles.action}>
        {props.isSelectMode ? (
          <button
            className="btn btn-danger"
            onClick={handleBulkDelete}
            disabled={props.calling}
          >
            削除
          </button>
        ) : (
          <>
            <button className={`btn ${styles.addBtn}`} onClick={addRow}>
              追加
            </button>
            <button
              className="btn btn-primary"
              onClick={saveFunc}
              disabled={props.calling}
            >
              登録
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentSpreadSheet;
